import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import type { SimulationAuthorization } from '@morpho-org/evm-simulation';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useMemo } from 'react';
import type { TransactionBuilder } from '@/contexts/Transaction/TransactionV2.context';
import { useVaultV2SimulatedShareDelta } from '@/components/VaultPage/VaultV2/hooks/useVaultV2SimulatedFinalPosition';
import { useWeb3Context } from '@/contexts/Web3Context';
import { resolveSimulatedFinalPosition } from '@/services/simulate/simulatedPosition.helpers';
import { useVaultV1 } from './useVaultV1';
import { useVaultV1Position } from './useVaultV1Position';
interface UseVaultV1SimulatedFinalPositionParams {
  vaultAddress: Address;
  /** The vault's chain (keys the position read); the simulation still runs on `targetChainId`. */
  chainId: ChainId | undefined;
  transaction: TransactionBuilder | undefined;
  simulationAuthorizations?: readonly SimulationAuthorization[];
  simulationKey?: ReadonlyArray<unknown>;
  isPreparing?: boolean;
  /** JS-math position-after, shown when the simulation is unavailable/unverified. */
  fallbackPosition?: bigint;
}

/**
 * Deposit/withdraw position-after derived from the EVM-simulated share delta
 * (`shares → toAssets`); falls back to the JS-math estimate when the simulation
 * is unavailable or unverified. Mirrors the Vault V2 flow.
 */
export function useVaultV1SimulatedFinalPosition(t0) {
  const $ = _c(25);
  const {
    vaultAddress,
    chainId,
    transaction,
    simulationAuthorizations,
    simulationKey,
    isPreparing: t1,
    fallbackPosition
  } = t0;
  const isPreparing = t1 === undefined ? false : t1;
  const {
    account,
    targetChainId
  } = useWeb3Context();
  const {
    vaultData
  } = useVaultV1(vaultAddress);
  let t2;
  if ($[0] !== chainId || $[1] !== vaultAddress) {
    t2 = {
      vaultAddress,
      chainId
    };
    $[0] = chainId;
    $[1] = vaultAddress;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    userVaultShares
  } = useVaultV1Position(t2);
  let t3;
  if ($[3] !== account.address || $[4] !== account.isWrongChain || $[5] !== isPreparing || $[6] !== simulationAuthorizations || $[7] !== simulationKey || $[8] !== targetChainId || $[9] !== transaction || $[10] !== vaultAddress) {
    t3 = {
      chainId: targetChainId,
      userAddress: account.address,
      isWrongChain: account.isWrongChain,
      vaultAddress,
      transaction,
      simulationAuthorizations,
      simulationKey,
      isPreparing
    };
    $[3] = account.address;
    $[4] = account.isWrongChain;
    $[5] = isPreparing;
    $[6] = simulationAuthorizations;
    $[7] = simulationKey;
    $[8] = targetChainId;
    $[9] = transaction;
    $[10] = vaultAddress;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    shareDelta,
    status,
    isLoading,
    isRefreshing
  } = useVaultV2SimulatedShareDelta(t3);
  let t4;
  bb0: {
    if (!isDefined(shareDelta) || !isDefined(userVaultShares) || !isDefined(vaultData)) {
      t4 = undefined;
      break bb0;
    }
    const finalShares = userVaultShares + shareDelta;
    let t5;
    if ($[12] !== finalShares || $[13] !== vaultData) {
      t5 = vaultData.toAssets(finalShares < ZERO ? ZERO : finalShares);
      $[12] = finalShares;
      $[13] = vaultData;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = t5;
  }
  const simulatedFinalPosition = t4;
  let t5;
  if ($[15] !== fallbackPosition || $[16] !== simulatedFinalPosition || $[17] !== status) {
    t5 = resolveSimulatedFinalPosition({
      status,
      simulatedValue: simulatedFinalPosition,
      fallbackPosition
    });
    $[15] = fallbackPosition;
    $[16] = simulatedFinalPosition;
    $[17] = status;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const {
    displayedFinalPosition,
    isUnverifiedFinalPosition
  } = t5;
  let t6;
  if ($[19] !== displayedFinalPosition || $[20] !== isLoading || $[21] !== isRefreshing || $[22] !== isUnverifiedFinalPosition || $[23] !== status) {
    t6 = {
      displayedFinalPosition,
      isUnverifiedFinalPosition,
      status,
      isLoading,
      isRefreshing
    };
    $[19] = displayedFinalPosition;
    $[20] = isLoading;
    $[21] = isRefreshing;
    $[22] = isUnverifiedFinalPosition;
    $[23] = status;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
}